import { useMemo, useCallback, useEffect, useState } from 'react';
import { Col, Row, Form, Badge } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import LinkWithParams from 'components/linkWithParams';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useNavigate } from 'hooks/useNavigate';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import BrandsApi from 'libs/api/promo/brands';
import SiteCategoriesApi from 'libs/api/admin/siteCategories';
import { useApi } from 'hooks/useApi';
import { useFilter } from 'hooks/useFilter';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';

const api = new BrandsApi();
const siteCategoriesApi = new SiteCategoriesApi();

export const statusColors = {
  Expired: 'black',
  Nuovo: 'yellow',
  status_to_admin: 'yellow',
  Approvato: 'green',
  Rifiutato: 'red',
  Disattiva: 'red',
};

/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{
 *  columns: any,
 *  data: any,
 *  singleItem: any,
 *  loading: boolean,
 *  updateInProcess: boolean,
 *  setUpdateInProcess: func,
 *  onDelete: func,
 *  onChangeTableFilter: func
 * }} state and callbacks for Brands page
 */
const useBrands = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const { filterData } = useFilter();
  const pages = useRecoilValue(getPage);
  const [siteCategories, setSiteCategories] = useState([]);
  const {
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    handleDownloadExcel,
    updateInProcess,
    setUpdateInProcess,
    handleApprove,
    setSingleItem,
    approveErrors,
  } = useApi({
    resourceKey: 'brands',
    singleResourceKey: 'brand',
    api,
    isMainPage,
  });

  const fetchSiteCategories = useCallback(async () => {
    try {
      const response = await siteCategoriesApi.list();
      setSiteCategories(response.data.site_categories || []);
    } catch (error) {
      console.error('Error fetching site categories:', error);
    }
  }, []);

  const handleApproveOrReject = useCallback(
    async ({ comment, publish, unpublish }) => {
      setUpdateInProcess(true);

      const data = { comment };

      if (publish) data.publish = publish;
      if (unpublish) data.unpublish = unpublish;

      try {
        if (!id) {
          navigate(UIHelper.getListRoute(pathname, 1));
          throw new Error('no ID provided.');
        } else {
          await api.handleApproveOrReject(id, { data });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        const errors = e.response?.data || [];
        form.setFields(ErrorHelper.getErrorsForForm(errors));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, pathname, id],
  );

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    try {
      const formData = new FormData();
      for (let key in values) {
        switch (key) {
          case 'site_category_ids': {
            if (values[key] && values[key].length > 0) {
              values[key].forEach((id) => {
                formData.append(`brand[${key}][]`, id);
              });
            }
            break;
          }
          case 'date_to':
          case 'date_from': {
            if (values[key]) {
              formData.append(`brand[${key}]`, values[key] || '');
            }
            break;
          }
          case 'header_images': {
            if (values[key] && values[key][0]) {
              values[key].forEach((file, index) => {
                formData.append(`brand[${key}][]`, file.originFileObj);
              });
            }
            break;
          }
          case 'logo':
          case 'header_image_offers':
            if (values[key] && values[key][0]) {
              formData.append(
                `brand[${key}]`,
                (values[key] &&
                  values[key][0] &&
                  values[key][0]?.originFileObj) ||
                  '',
              );
            }
            break;
          case 'on_carousel':
            formData.append(`brand[${key}]`, values[key] ? 'true' : 'false');
            break;
          default:
            formData.append(`brand[${key}]`, values[key] || '');
            break;
        }
      }
      if (!id) {
        await api.createProduct(formData);
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.updateProduct(id, formData);
        navigate(UIHelper.getListRoute(pathname, 2));
      }
    } catch (e) {
      const errors = e.response?.data || {};
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          description: 'description_it',
          company: 'company_id',
        }),
      );
      if (errors?.attachments?.length) {
        UIHelper.openNotificationWithIcon(
          'error',
          errors?.attachments?.join(', '),
        );
      }
    } finally {
      setUpdateInProcess(false);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        sorter: () => {},
        fixed: 'left',
        render: (data, row) => row?.shopping_center_title,
      },
      {
        title: t('Insegna'),
        dataIndex: 'company.title',
        sorter: () => {},
        render: (data, row) => row?.company,
      },
      {
        title: t('Data Inizio'),
        dataIndex: 'date_from',
        render: (data) => data && moment(data).format('DD MMMM YYYY'),
        sorter: () => {},
      },
      {
        title: t('Data fine'),
        dataIndex: 'date_to',
        render: (data) => data && moment(data).format('DD MMMM YYYY'),
        sorter: () => {},
      },
      {
        title: t('Stato'),
        dataIndex: 'published',
        render: (data) => {
          const statusKey = data === 'Creato' ? 'Nuovo' : data;
          return <Badge color={statusColors[statusKey]} text={t(statusKey)} />;
        },
        sorter: () => {},
      },
      {
        title: t('Retail approvato'),
        dataIndex: 'retail_approved',
        render: (data) => {
          const statusKey = data === 'Creato' ? 'Nuovo' : data;
          return <Badge color={statusColors[statusKey]} text={t(statusKey)} />;
        },
        sorter: () => {},
      },
      {
        title: t('Marketing approvato'),
        dataIndex: 'marketing_approved',
        render: (data) => {
          const statusKey = data === 'Creato' ? 'Nuovo' : data;
          return <Badge color={statusColors[statusKey]} text={t(statusKey)} />;
        },
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update ||
            row.can_approve ||
            row.can_destroy) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {(row.can_update || row.can_approve) && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy && (
                <Col>
                  <LinkWithParams to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </LinkWithParams>
                </Col>
              )}
            </Row>
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Catena'),
        type: 'select',
        name: 'by_chain',
        values: filterData?.chains?.map((chain) => ({
          title: chain.title,
          value: chain.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company_id',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Dal'),
        type: 'date',
        name: 'by_date_from',
        values: [],
      },
      {
        title: t('AI'),
        type: 'date',
        name: 'by_date_to',
        values: [],
      },
    ],
    [t, filterData],
  );

  useEffect(() => {
    if (!isMainPage) {
      fetchSiteCategories();
    }
  }, [isMainPage, fetchSiteCategories]);

  useEffect(() => {
    if (id && !isMainPage) {
      const values = [
        {
          name: 'title',
          value: singleItem?.title,
        },
        {
          name: 'description_it',
          value: singleItem?.description_it,
        },
        {
          name: 'description_en',
          value: singleItem?.description_en,
        },
        {
          name: 'description_fr',
          value: singleItem?.description_fr,
        },
        {
          name: 'description_short_it',
          value: singleItem?.description_short_it,
        },
        {
          name: 'description_short_en',
          value: singleItem?.description_short_en,
        },
        {
          name: 'description_short_fr',
          value: singleItem?.description_short_fr,
        },
        {
          name: 'phone',
          value: singleItem?.phone,
        },
        {
          name: 'whatsapp',
          value: singleItem?.whatsapp,
        },
        {
          name: 'until_last',
          value: singleItem?.until_last,
        },
        {
          name: 'staff_only',
          value: singleItem?.staff_only,
        },
        {
          name: 'evidence',
          value: singleItem?.evidence,
        },
        {
          name: 'company_id',
          value: singleItem?.company_id,
        },
        {
          name: 'site_category_ids',
          value:
            singleItem?.site_categories?.map((category) => category.id) || [],
        },
        {
          name: 'mapedin_id',
          value: singleItem?.mapedin_id || '',
        },
        {
          name: 'working_hours',
          value: singleItem?.working_hours || '',
        },
        {
          name: 'on_carousel',
          value: singleItem?.on_carousel || false,
        },
      ];

      singleItem?.date_from &&
        values.push({
          name: 'date_from',
          value: moment(singleItem?.date_from),
        });

      singleItem?.date_to &&
        values.push({
          name: 'date_to',
          value: moment(singleItem?.date_to),
        });

      form.setFields(values);
    }
  }, [singleItem, form, id, isMainPage]);

  const handleDeleteFile = async (data) => {
    setUpdateInProcess(true);
    try {
      const {
        file: { id },
        param,
      } = data;
      await api.deleteFile(singleItem.id, id);
      if (param === 'header_images') {
        setSingleItem((s) => ({
          ...s,
          allData: {
            ...s.allData,
            header_images: s.allData.header_images.filter(
              (image) => image.id !== id,
            ),
          },
        }));
      } else {
        // Для всіх інших полів (logo, header_image_offers) просто встановлюємо null
        setSingleItem((s) => ({
          ...s,
          allData: {
            ...s.allData,
            [`${param}`]: null,
            [`${param}_name`]: null,
            [`${param}_id`]: null,
          },
        }));
      }
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    form,
    columns,
    filters,
    data,
    singleItem,
    loading,
    onDelete,
    updateInProcess,
    handleCreateOrUpdate,
    handleApproveOrReject,
    handleDownloadExcel,
    onChangeTableFilter,
    handleApprove,
    handleDeleteFile,
    approveErrors,
    filterData,
    siteCategories: filterData?.site_categories || [],
  };
};

export default useBrands;
